import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { LogResponse } from '../../types';
import { getLogEntry, getLogs } from '../../lib/admin';
import { useAuth } from '../../utils/authorization';
import { Refresh } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import Pagination from '../global/Pagination';
import LogDetails from './LogDetails';
import LogFilterSelect from './LogFilterSelect';
import { formatLogDate, getLogAction, getLogLabel, getLogTarget, LOG_SEARCH_SCOPES, LOG_SECTIONS, LOGS_PAGE_SIZE, toRangeBoundary } from './logUtils';

type ActivityLogViewProps = {
    storeId?: number;
    entityTypes: string[];
    heading: ReactNode;
};

const hasAffectedUsers = (entityTypes: string[]) => entityTypes.includes('user');

const hasSections = (entityTypes: string[]) => entityTypes.some((type) => type in LOG_SECTIONS);

const EMPTY_FILTERS = {
    entityType: '',
    section: '',
    userSearch: '',
    searchIn: '',
    fromDateTime: '',
    toDateTime: '',
};

export default function ActivityLogView({ storeId, entityTypes, heading }: ActivityLogViewProps) {
    const { user } = useAuth();
    const isUtc = !!user?.localTimezone;

    const [logs, setLogs] = useState<LogResponse[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [searchedEmail, setSearchedEmail] = useState('');
    const [page, setPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [details, setDetails] = useState<Record<string, LogResponse | null>>({});
    const [refreshCount, setRefreshCount] = useState(0);
    const pendingDetails = useRef(new Set<string>());

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchedEmail(filters.userSearch.trim());
            setPage(1);
        }, 400);

        return () => clearTimeout(timeout);
    }, [filters.userSearch]);

    useEffect(() => {
        let isCancelled = false;
        setIsLoading(true);

        getLogs(storeId, {
            entityType: filters.entityType || undefined,
            section: filters.section || undefined,
            userEmail: searchedEmail || undefined,
            searchIn: filters.searchIn || undefined,
            from: toRangeBoundary(filters.fromDateTime, 'start', isUtc),
            to: toRangeBoundary(filters.toDateTime, 'end', isUtc),
            limit: LOGS_PAGE_SIZE,
            page,
        }).then(({ items, total }) => {
            if (isCancelled) return;
            setLogs(items);
            setTotal(total);
            setIsLoading(false);
        });

        return () => {
            isCancelled = true;
        };
    }, [storeId, filters.entityType, filters.section, filters.searchIn, filters.fromDateTime, filters.toDateTime, searchedEmail, page, isUtc, refreshCount]);

    const hasActiveFilters = Object.values(filters).some(Boolean);
    const canSearchByScope = hasAffectedUsers(entityTypes) && !!filters.userSearch.trim();
    const sections = LOG_SECTIONS[filters.entityType] || [];
    const showSectionColumn = hasSections(entityTypes);

    const updateFilter = (key: keyof typeof filters, value: string) => {
        setFilters((current) => ({
            ...current,
            [key]: value,
            ...(key === 'entityType' && { section: '' }),
        }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setPage(1);
    };

    const paginate = (pageNumber: number) => {
        setPage(pageNumber);
        setExpandedRow(null);
    };

    const loadDetails = (logId: string) => {
        if (details[logId] || pendingDetails.current.has(logId)) return;

        pendingDetails.current.add(logId);
        getLogEntry(logId).then((entry) => {
            pendingDetails.current.delete(logId);
            setDetails((current) => ({ ...current, [logId]: entry }));
        });
    };

    const toggleDetails = (logId: string) => {
        if (expandedRow === logId) {
            setExpandedRow(null);
            return;
        }

        setExpandedRow(logId);
        loadDetails(logId);
    };

    return (
        <div className="activity-log">
            <div className="activity-log__header">
                {heading}
                <button
                    type="button"
                    className="activity-log__refresh"
                    onClick={() => setRefreshCount((count) => count + 1)}
                >
                    <Refresh /> Refresh
                </button>
            </div>

            <div className="activity-log__filters">
                <div className="activity-log__field activity-log__field--search">
                    <span>User</span>
                    <input
                        type="search"
                        className="activity-log__input activity-log__input--search"
                        placeholder="Search by email"
                        value={filters.userSearch}
                        onChange={(e) => updateFilter('userSearch', e.target.value)}
                    />
                    {canSearchByScope && (
                        <div className="activity-log__scope">
                            {LOG_SEARCH_SCOPES.map((scope) => (
                                <button
                                    key={scope.value}
                                    type="button"
                                    className={`activity-log__scope-option ${filters.searchIn === scope.value ? 'is-selected' : ''}`}
                                    onClick={() => updateFilter('searchIn', scope.value)}
                                >
                                    {scope.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <label className="activity-log__field">
                    <span>Entity</span>
                    <LogFilterSelect
                        value={filters.entityType}
                        options={entityTypes}
                        allLabel="All entities"
                        onChange={(value) => updateFilter('entityType', value)}
                    />
                </label>
                {sections.length > 0 && (
                    <label className="activity-log__field">
                        <span>Section</span>
                        <LogFilterSelect
                            value={filters.section}
                            options={sections}
                            allLabel="All sections"
                            onChange={(value) => updateFilter('section', value)}
                        />
                    </label>
                )}
                <label className="activity-log__field activity-log__field--date">
                    <span>From</span>
                    <input
                        type="datetime-local"
                        className="activity-log__input"
                        value={filters.fromDateTime}
                        max={filters.toDateTime || undefined}
                        onChange={(e) => updateFilter('fromDateTime', e.target.value)}
                    />
                </label>
                <label className="activity-log__field activity-log__field--date">
                    <span>To</span>
                    <input
                        type="datetime-local"
                        className="activity-log__input"
                        value={filters.toDateTime}
                        min={filters.fromDateTime || undefined}
                        onChange={(e) => updateFilter('toDateTime', e.target.value)}
                    />
                </label>
                <button type="button" className="activity-log__clear" onClick={clearFilters} disabled={!hasActiveFilters}>
                    Clear
                </button>
            </div>

            <div className={`activity-log__table-box ${isLoading && logs.length > 0 ? 'activity-log__table-box--loading' : ''}`}>
                {isLoading && logs.length > 0 && <LinearProgress className="activity-log__progress" />}
                {isLoading && logs.length === 0 ? (
                    <div className="activity-log__empty">Loading…</div>
                ) : logs.length === 0 ? (
                    <div className="activity-log__empty">No activity found.</div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Date {isUtc ? '(UTC)' : ''}</th>
                                <th>User</th>
                                <th>Entity</th>
                                {showSectionColumn && <th>Section</th>}
                                <th>Action</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, index) => {
                                const logId = log._id || '';
                                const isExpanded = !!logId && expandedRow === logId;
                                const target = getLogTarget(log);

                                return (
                                    <React.Fragment key={logId || `${log.createdAt}-${index}`}>
                                        <tr>
                                            <td className="activity-log__date">{formatLogDate(log.createdAt, isUtc)}</td>
                                            <td>{log.userEmail || 'Unknown'}</td>
                                            <td><span className="activity-log__pill">{getLogLabel(log.entityType)}</span></td>
                                            {showSectionColumn && <td>{getLogLabel(log.section)}</td>}
                                            <td>
                                                {getLogAction(log)}
                                                {target && (
                                                    <span className="activity-log__target">&nbsp;→ {target}</span>
                                                )}
                                            </td>
                                            <td className="activity-log__details-col">
                                                {logId && (
                                                    <button
                                                        type="button"
                                                        className="activity-log__details-btn"
                                                        onClick={() => toggleDetails(logId)}
                                                        onMouseEnter={() => loadDetails(logId)}
                                                        onFocus={() => loadDetails(logId)}
                                                    >
                                                        {isExpanded ? 'Hide details' : 'Details'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="activity-log__details-row">
                                                <td colSpan={showSectionColumn ? 6 : 5}>
                                                    <LogDetails
                                                        entry={details[logId]}
                                                        isLoading={!(logId in details) || pendingDetails.current.has(logId)}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {total > 0 && (
                <div className="activity-log__pagination">
                    <Pagination
                        pageSize={LOGS_PAGE_SIZE}
                        totalItems={total}
                        paginate={paginate}
                        currentPage={page}
                        disableEdges
                    />
                </div>
            )}
        </div>
    );
}
